import _ from 'lodash';

export default class Route {
	constructor (method, path, component = null, children = []) {
		this.method = method;
		this.path = path;
		this._component = component;
		this._name = null;
		this._redirect = null;
		this._meta = {};
		this._children = children;
	}

	/**
	 * Returns a Route instance with the "GET" method.
	 *
	 * @param {string} path The URL path
	 * @param {function|string} component The Vue Component to load, or component path.
	 * @returns {Route}
	 */
	static get (path, component = null) {
		return new Route('get', path, component);
	}

	/**
	 *
	 * @param {striung} prefix The string to prepend to child paths
	 * @param {object} component The Vue layout component
	 * @param {array} children An array of routes
	 * @returns {Route}
	 */
	static group (prefix, component, children) {
		return new Route('group', prefix, component, children);
	}

	/**
	 * Adds to the middleware on this route.
	 *
	 * @param {array} middleware An array of middleware objects
	 * @returns {Route}
	 */
	middleware (middleware) {
		if (this.method === 'group') {
			// Set middleware of children
			for (const child of this._children) {
				/*
				 * using this routing mechanism puts limits on the full capability
				 * of the Vue routing system. I am staring a refactor on this
				 * to allow for the more robust vue routing system starting with
				 * new routes, and eventually transitioning existing routes
				 *
				 * for backward compatibility, if the route is an instance of this class,
				 * middleware will be appended as it always has, otherwise, the middleware will need to
				 * be added to the route inside the meta property
				 */

				if (child instanceof Route) {
					child.middleware(middleware);
				}
			}
		}
		else {
			if (!this._meta.hasOwnProperty('middleware')) {
				this._meta.middleware = [];
			}

			this._meta.middleware = this._meta.middleware.concat(middleware);
		}

		return this;
	}

	/**
	 * Sets the name of this Route.
	 *
	 * @param {string} name
	 * @returns {Route}
	 */
	name (name) {
		this._name = name;
		return this;
	}

	/**
	 * Sets the metadata of this Route.
	 *
	 * @param {string|object} key The meta key, or a key/value list of metadata.
	 * @param {mixed} value
	 * @returns {Route}
	 */
	meta (key, value) {
		if (this.method === 'group') {
			for (const child of this._children) {
				/*
				 * using this routing mechanism puts limits on the full capability
				 * of the Vue routing system. I am staring a refactor on this
				 * to allow for the more robust vue routing system starting with
				 * new routes, and eventually transitioning existing routes
				 *
				 * for backward compatibility, if the route is an instance of this class,
				 * meta will be appended as it always has, otherwise, the meta will need to
				 * be added to the route
				 */

				if (child instanceof Route) {
					child.meta(key, value);
				}
			}
		}
		else {
			if (_.isObject(key)) {
				// Loop through each and set the meta tag
				for (const meta in key) {
					this._meta[meta] = key[meta];
				}
			}
			else {
				this._meta[key] = value;
			}
		}

		return this;
	}

	/**
	 * Sets the current route to redirect to the given path.
	 * @param {string} path The path to redirect to
	 */
	redirect (path) {
		this._redirect = path;

		return this;
	}

	/**
	 * Returns the Route object that is understood by the VueRouter.
	 *
	 * @returns {object}
	 */
	register () {
		if (this.method === 'group') {
			return this.registerGroup();
		}

		const route = { path: this.path };

		if (this._component !== null) {
			route.component = this._component;
		}

		if (this._redirect !== null) {
			route.redirect = this._redirect;
		}

		if (!_.isEmpty(this._meta)) {
			route.meta = this._meta;
		}

		if (!_.isEmpty(this._name)) {
			route.name = this._name;
		}

		return route;
	}

	registerGroup () {
		return {
			path: this.path,
			component: this._component,
			children: this._children.map(childRoute => {
				/*
				 * using this routing mechanism puts limits on the full capability
				 * of the Vue routing system. I am staring a refactor on this
				 * to allow for the more robust vue routing system starting with
				 * new routes, and eventually transitioning existing routes
				 *
				 * for backward compatibility, if the route is an instance of this class,
				 * the route will be registered as it was, otherwise, we will take the object
				 * provided expecting it to be formatted vue route
				 */

				if (childRoute instanceof Route) {
					return childRoute.register();
				}
				else {
					return childRoute;
				}
			})
		};
	}
}
